/**
 * Node trust state for the Settings section: state.ext.trust from the store, kept live by the
 * hub's SSE 'ext' events, and the answer of every change. GET /api/trust on mount and on
 * Retry; polled every 5 s only when the store has no trust slice (a hub without the module's
 * live state). In demo mode (?mock=1) it runs on local data.
 */
import { useCallback, useEffect, useState } from 'react';
import { REMOTE_MACHINES_WARNING } from '@orgus/protocol';
import type { AuditVerifyResult, Project, TrustSettingsPatch, TrustState } from '@orgus/protocol';
import { api } from '../../lib/api.ts';
import { useOrgus, useExt } from '../../lib/store.ts';
import { mockTrust, mockVerify } from './mock.ts';

const POLL_MS = 5_000;

export interface TrustApi {
  state: TrustState | null;
  loading: boolean;
  error: unknown;
  /** When the state was last fetched (for the live label). */
  fetchedAt: number | null;
  refresh(): Promise<void>;
  freeze(reason: string): Promise<void>;
  unfreeze(): Promise<void>;
  pause(projectId: string, reason: string): Promise<void>;
  resume(projectId: string): Promise<void>;
  saveSettings(patch: TrustSettingsPatch): Promise<void>;
  verifyAudit(): Promise<AuditVerifyResult>;
  /** COMPAT-3: bind a machine's waiting key (the fingerprint the machine itself shows). */
  approveKey(machineId: string, fingerprint: string): Promise<void>;
  dismissKey(machineId: string): Promise<void>;
  /** RN-SD3-FORK-1: end a machine's quarantine once the real computer is known. */
  release(machineId: string): Promise<void>;
}

export function useTrust(): TrustApi {
  const mode = useOrgus((s) => s.mode);
  const machines = useOrgus((s) => s.machines);
  const [state, setState] = useState<TrustState | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const mock = mode === 'mock';
  const live = useExt<TrustState>('trust');

  const accept = useCallback((s: TrustState) => {
    setState(s);
    setError(null);
    setFetchedAt(Date.now());
  }, []);

  const refresh = useCallback(async () => {
    if (mock) {
      // Built once the demo machines exist (the effect below).
      setFetchedAt(Date.now());
      return;
    }
    try {
      accept(await api<TrustState>('/api/trust'));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [mock, accept]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Live: every change the hub pushes replaces the state. Without it, poll.
  const hasLive = !mock && !!live;
  useEffect(() => {
    if (hasLive && live) {
      accept(live);
      setLoading(false);
    }
  }, [hasLive, live, accept]);
  useEffect(() => {
    if (mock || hasLive) return;
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [refresh, mock, hasLive]);

  // Demo mode: build the state once the demo machines exist.
  useEffect(() => {
    if (!mock || state || !Object.keys(machines).length) return;
    setState(mockTrust(Object.values(machines)));
    setLoading(false);
  }, [mock, machines, state]);

  const local = (change: (s: TrustState) => TrustState) => {
    setState((s) => (s ? change(s) : s));
    return Promise.resolve();
  };
  const by = 'owner';

  return {
    state,
    loading,
    error,
    fetchedAt,
    refresh,
    freeze: async (reason) =>
      mock ? local((s) => ({ ...s, frozen: { by, at: Date.now(), reason } })) : accept(await api<TrustState>('/api/trust/freeze', { method: 'POST', body: { reason } })),
    unfreeze: async () => (mock ? local((s) => ({ ...s, frozen: null })) : accept(await api<TrustState>('/api/trust/freeze', { method: 'DELETE' }))),
    pause: async (projectId, reason) =>
      mock
        ? local((s) => ({ ...s, pausedProjects: [...s.pausedProjects.filter((p) => p.projectId !== projectId), { projectId, by, at: Date.now(), reason }] }))
        : accept(await api<TrustState>(`/api/trust/projects/${encodeURIComponent(projectId)}/pause`, { method: 'POST', body: { reason } })),
    resume: async (projectId) =>
      mock
        ? local((s) => ({ ...s, pausedProjects: s.pausedProjects.filter((p) => p.projectId !== projectId) }))
        : accept(await api<TrustState>(`/api/trust/projects/${encodeURIComponent(projectId)}/pause`, { method: 'DELETE' })),
    saveSettings: async (patch) =>
      mock
        ? local(({ remoteMachines, relay, ...s }) => {
            const { remoteMachines: remote, relay: relayOn, ...rest } = patch;
            return {
              ...s,
              ...rest,
              minAgentVersion: patch.minAgentVersion === undefined ? s.minAgentVersion : patch.minAgentVersion,
              remoteMachines: remote === undefined ? remoteMachines : { allowed: remote, warning: remote ? REMOTE_MACHINES_WARNING : null, legacy: false },
              relay: relayOn === undefined ? relay : relayOn ? { on: true, link: 'connected', since: Date.now(), error: null } : { on: false, link: 'off', since: null, error: null },
            };
          })
        : accept(await api<TrustState>('/api/trust/settings', { method: 'PUT', body: patch })),
    verifyAudit: async () => (mock ? mockVerify(state) : api<AuditVerifyResult>('/api/trust/audit/verify')),
    approveKey: async (machineId, fingerprint) =>
      mock
        ? local((s) => ({ ...s, nodes: s.nodes.map((n) => (n.machineId === machineId ? { ...n, status: 'key', fingerprint, storage: n.pendingKey?.storage ?? 'file', registeredAt: Date.now(), pendingKey: null } : n)) }))
        : accept(await api<TrustState>(`/api/trust/nodes/${encodeURIComponent(machineId)}/key`, { method: 'POST', body: { fingerprint } })),
    dismissKey: async (machineId) =>
      mock
        ? local((s) => ({ ...s, nodes: s.nodes.map((n) => (n.machineId === machineId ? { ...n, pendingKey: null } : n)) }))
        : accept(await api<TrustState>(`/api/trust/nodes/${encodeURIComponent(machineId)}/key`, { method: 'DELETE' })),
    release: async (machineId) =>
      mock
        ? local((s) => ({ ...s, nodes: s.nodes.map((n) => (n.machineId === machineId ? { ...n, quarantine: null } : n)) }))
        : accept(await api<TrustState>(`/api/trust/nodes/${encodeURIComponent(machineId)}/release`, { method: 'POST' })),
  };
}

/** Projects that can be paused (known to the hub, not paused yet), by name. */
export function pausable(projects: Record<string, Project>, s: TrustState | null): Project[] {
  const paused = new Set(s?.pausedProjects.map((p) => p.projectId) ?? []);
  return Object.values(projects)
    .filter((p) => !paused.has(p.id))
    .sort((a, b) => a.name.localeCompare(b.name));
}
